import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { attendanceAPI, usersAPI, subscriptionsAPI } from '../../lib/api';
import { toast } from 'sonner';
import {
  Search, Plus, Trash2, Calendar, MapPin,
  User, Check, X, FileSpreadsheet, AlertCircle,
  Eye, Baby, CreditCard, Clock, Mail,
  ChevronLeft, ChevronRight, Filter
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { Calendar as CalendarPicker } from '../../components/ui/calendar';
import { format, isToday, isYesterday, subDays, addDays, parseISO } from 'date-fns';
import { ro } from 'date-fns/locale';

const LOCATIONS = [
  { id: "fiald", name: 'Bazin Fiald (Hotel Fiald)' },
  { id: "olimpic", name: 'Bazinul Olimpic Bacău' },
  { id: "emd", name: 'Bazinul EMD Academy' }
];

// Program acceptat: 07:00 - 21:00. Grupăm prezențele pe intervale orare (ex: "14:00 - 15:00").
const OPEN_HOUR = 7;
const CLOSE_HOUR = 21;

const getTimeframe = (date) => {
  const h = date.getHours();
  if (h < OPEN_HOUR || h > CLOSE_HOUR || (h === CLOSE_HOUR && date.getMinutes() > 0)) {
    return 'Alt interval';
  }
  const start = Math.min(h, CLOSE_HOUR - 1); // 21:00 fix intră în ultimul interval
  return `${String(start).padStart(2, '0')}:00 - ${String(start + 1).padStart(2, '0')}:00`;
};

const formatDayLabel = (dateStr) => {
  const d = new Date(dateStr);
  if (isToday(d)) return 'Azi';
  if (isYesterday(d)) return 'Ieri';
  return format(d, 'EEEE, dd MMMM yyyy', { locale: ro });
};

// Group attendance: [ { dayKey, label, timeframes: [ { label, entries: [...] } ] } ]
const groupAttendance = (records) => {
  const byDay = {};
  for (const att of records) {
    const d = new Date(att.recorded_at);
    const dayKey = format(d, 'yyyy-MM-dd');
    if (!byDay[dayKey]) byDay[dayKey] = {};
    const tf = getTimeframe(d);
    if (!byDay[dayKey][tf]) byDay[dayKey][tf] = [];
    byDay[dayKey][tf].push(att);
  }
  return Object.entries(byDay)
    .sort((a, b) => b[0].localeCompare(a[0]))
    .map(([dayKey, tfs]) => ({
      dayKey,
      label: formatDayLabel(dayKey),
      timeframes: Object.entries(tfs)
        .sort((a, b) => {
          // sort timeframes by first entry time desc
          const aTime = Math.max(...a[1].map(e => new Date(e.recorded_at).getTime()));
          const bTime = Math.max(...b[1].map(e => new Date(e.recorded_at).getTime()));
          return bTime - aTime;
        })
        .map(([tf, entries]) => ({ label: tf, entries })),
    }));
};

const AttendancePage = () => {
  const navigate = useNavigate();
  const [attendance, setAttendance] = useState([]);
  const [activeUsers, setActiveUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedPersons, setSelectedPersons] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(LOCATIONS[0].name);
  const [customDatetime, setCustomDatetime] = useState('');
  const [searchUsers, setSearchUsers] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [saving, setSaving] = useState(false);
  const [emailSending, setEmailSending] = useState(false);

  // Day navigation - default: today
  const todayStr = format(new Date(), 'yyyy-MM-dd');
  const [selectedDate, setSelectedDate] = useState(todayStr);

  // Export date range
  const [exportStart, setExportStart] = useState('');
  const [exportEnd, setExportEnd] = useState('');
  const [showExportFilter, setShowExportFilter] = useState(false);

  // Popup state
  const [popupAtt, setPopupAtt] = useState(null);
  const [popupLoading, setPopupLoading] = useState(false);
  const [popupData, setPopupData] = useState(null); // { subs, user }

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const start = new Date(selectedDate + 'T00:00:00').toISOString();
      const end = new Date(selectedDate + 'T23:59:59').toISOString();
      const res = await attendanceAPI.getAll({ start_date: start, end_date: end, limit: 500 });
      setAttendance(res.data.data || []);
    } catch (error) {
      toast.error('Eroare la încărcarea datelor');
    } finally {
      setLoading(false);
    }
  }, [selectedDate]);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    const fetchActive = async () => {
      try {
        const res = await usersAPI.getWithActiveSubscriptions({ search: searchUsers });
        setActiveUsers(res.data);
      } catch {
        console.error('Failed to fetch active users');
      }
    };
    if (showAddModal) {
      const timer = setTimeout(fetchActive, 300);
      return () => clearTimeout(timer);
    }
  }, [showAddModal, searchUsers]);

  const handleRowClick = async (att) => {
    setPopupAtt(att);
    setPopupData(null);
    setPopupLoading(true);
    try {
      const [subsRes, userRes] = await Promise.all([
        subscriptionsAPI.getForPerson(att.person_id, att.person_type),
        usersAPI.getOne(att.user_id),
      ]);
      setPopupData({ subs: subsRes.data, user: userRes.data });
    } catch {
      setPopupData({ subs: [], user: null });
    } finally {
      setPopupLoading(false);
    }
  };

  const handleRecordAttendance = async () => {
    if (selectedPersons.length === 0) return;
    setSaving(true);
    const errors = [];
    for (const p of selectedPersons) {
      try {
        await attendanceAPI.recordManual({
          person_id: p.person_id,
          person_type: p.person_type,
          location: selectedLocation,
          method: 'manual',
          ...(customDatetime ? { custom_datetime: new Date(customDatetime).toISOString() } : {}),
        });
      } catch (error) {
        const msg = error.response?.data?.detail || p.person_name;
        errors.push(msg);
      }
    }
    setSaving(false);
    if (errors.length === 0) {
      toast.success(`${selectedPersons.length > 1 ? `${selectedPersons.length} prezențe` : 'Prezență'} înregistrate`);
    } else if (errors.length < selectedPersons.length) {
      toast.success('Prezențe parțial înregistrate');
      errors.forEach(e => toast.error(e));
    } else {
      errors.forEach(e => toast.error(e));
    }
    setShowAddModal(false);
    setSelectedPersons([]);
    setCustomDatetime('');
    fetchData();
  };

  const handleEmailExport = async () => {
    setEmailSending(true);
    try {
      const params = {};
      if (exportStart) params.start_date = new Date(exportStart).toISOString();
      if (exportEnd) params.end_date = new Date(exportEnd + 'T23:59:59').toISOString();
      const res = await attendanceAPI.emailExport(params);
      toast.success(res.data.message || 'Export trimis pe email');
    } catch {
      toast.error('Eroare la trimiterea exportului');
    } finally {
      setEmailSending(false);
    }
  };

  const handleExport = async () => {
    try {
      const params = {};
      if (exportStart) params.start_date = new Date(exportStart).toISOString();
      if (exportEnd) params.end_date = new Date(exportEnd + 'T23:59:59').toISOString();
      const response = await attendanceAPI.export(params);
      const headers = ['Nume', 'Locație', 'Metodă', 'Data'];
      const rows = response.data.map(a => [
        a.person_name, a.location, a.method || '',
        format(new Date(a.recorded_at), 'dd.MM.yyyy HH:mm')
      ]);
      const escape = (v) => String(v || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
      const xmlRows = [headers, ...rows].map(row =>
        `<Row>${row.map(cell => `<Cell><Data ss:Type="String">${escape(cell)}</Data></Cell>`).join('')}</Row>`
      ).join('\n');
      const xml = `<?xml version="1.0" encoding="UTF-8"?><?mso-application progid="Excel.Sheet"?><Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet" xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet"><Styles><Style ss:ID="h"><Font ss:Bold="1"/></Style></Styles><Worksheet ss:Name="Prezente"><Table>${xmlRows}</Table></Worksheet></Workbook>`;
      const blob = new Blob([xml], { type: 'application/vnd.ms-excel;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `prezente_nautica_${format(new Date(), 'yyyy-MM-dd')}.xls`;
      a.click();
      toast.success('Export Excel descărcat');
    } catch {
      toast.error('Eroare la export');
    }
  };

  const grouped = useMemo(() => groupAttendance(attendance), [attendance]);

  // Active subscription from popup data
  const activeSub = popupData?.subs?.find(s => s.status === 'active');
  const parentName = popupAtt?.person_type === 'child' ? popupData?.user?.name : null;
  const parentEmail = popupAtt?.person_type === 'child' ? popupData?.user?.email : null;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="font-heading text-4xl font-bold text-white uppercase tracking-tight">Prezență</h1>
          <p className="text-white/40 text-base mt-1">Istoricul accesului în bazine</p>
        </div>
        <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-2 sm:gap-3 w-full sm:w-auto">
          <button
            onClick={() => setShowExportFilter(v => !v)}
            className="flex items-center justify-center gap-2 px-3 sm:px-4 py-2.5 bg-white/5 border border-white/10 text-white/50 text-xs sm:text-sm font-bold uppercase tracking-wider hover:bg-white/10 transition-colors rounded"
          >
            <Filter size={14} /> Perioadă
          </button>
          <button onClick={handleExport} className="flex items-center justify-center gap-2 px-3 sm:px-4 py-2.5 bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-xs sm:text-sm font-bold uppercase tracking-wider hover:bg-emerald-500/20 transition-colors rounded">
            <FileSpreadsheet size={14} /> Excel
          </button>
          <button
            onClick={handleEmailExport}
            disabled={emailSending}
            className="flex items-center justify-center gap-2 px-3 sm:px-4 py-2.5 bg-blue-500/10 border border-blue-500/20 text-blue-400 text-xs sm:text-sm font-bold uppercase tracking-wider hover:bg-blue-500/20 transition-colors rounded disabled:opacity-40"
          >
            <Mail size={14} /> {emailSending ? 'Se trimite...' : 'Pe Email'}
          </button>
          <button onClick={() => setShowAddModal(true)} className="btn-primary px-3 sm:px-4 py-2.5 flex items-center justify-center gap-2 text-xs sm:text-sm font-bold uppercase tracking-widest">
            <Plus size={16} /> Înregistrare
          </button>
        </div>
      </div>

      {showExportFilter && (
        <div className="bg-[#0A0A0A] border border-white/5 p-4 flex flex-wrap items-end gap-4">
          <div className="space-y-1">
            <label className="text-white/30 text-[11px] font-black uppercase tracking-widest block">De la data</label>
            <input
              type="date"
              value={exportStart}
              onChange={e => setExportStart(e.target.value)}
              className="bg-[#121212] border border-white/10 p-2.5 text-white text-base focus:border-[#6db025] outline-none"
            />
          </div>
          <div className="space-y-1">
            <label className="text-white/30 text-[11px] font-black uppercase tracking-widest block">Până la data</label>
            <input
              type="date"
              value={exportEnd}
              onChange={e => setExportEnd(e.target.value)}
              className="bg-[#121212] border border-white/10 p-2.5 text-white text-base focus:border-[#6db025] outline-none"
            />
          </div>
          {(exportStart || exportEnd) && (
            <button
              onClick={() => { setExportStart(''); setExportEnd(''); }}
              className="text-[11px] text-white/30 hover:text-red-400 font-black uppercase tracking-widest transition-colors pb-2.5"
            >
              ✕ Resetează
            </button>
          )}
          <p className="text-white/20 text-[11px] uppercase font-bold tracking-widest pb-2.5">
            {exportStart || exportEnd ? 'Intervalul se aplică la ambele exporturi (Excel & Email)' : 'Fără interval = toate înregistrările'}
          </p>
        </div>
      )}

      {/* Day navigation */}
      <div className="bg-[#0A0A0A] border border-white/5">
        <div className="flex items-center justify-between px-5 py-4 gap-3">
          <button
            onClick={() => setSelectedDate(format(subDays(parseISO(selectedDate), 1), 'yyyy-MM-dd'))}
            className="flex items-center justify-center gap-2 px-3 sm:px-4 py-2.5 bg-white/5 hover:bg-white/10 text-white/60 hover:text-white text-base font-black uppercase tracking-widest transition-colors rounded"
            aria-label="Ziua anterioară"
          >
            <ChevronLeft size={22} /> <span className="hidden sm:inline">Ziua anterioară</span>
          </button>

          <div className="flex flex-col items-center gap-1.5 min-w-0">
            <div className="flex items-center gap-3 flex-wrap justify-center">
              <span className="text-white font-black uppercase tracking-widest text-lg sm:text-xl">
                {format(parseISO(selectedDate), 'dd MMM yyyy', { locale: ro }).toUpperCase()}
              </span>
              {selectedDate === todayStr ? (
                <span className="px-2.5 py-1 bg-[#6db025] text-black text-sm font-black uppercase tracking-widest rounded-sm">
                  Azi
                </span>
              ) : (
                <button
                  onClick={() => setSelectedDate(todayStr)}
                  className="px-2.5 py-1 bg-[#6db025]/10 border border-[#6db025]/20 text-[#6db025] text-sm font-black uppercase tracking-widest hover:bg-[#6db025]/20 transition-colors rounded-sm"
                >
                  Azi
                </button>
              )}
            </div>
            <div className="flex items-center gap-3">
              <span className="text-white/40 text-sm font-bold uppercase tracking-widest">
                {loading ? '…' : `${attendance.length} intrări`}
              </span>
              <Popover>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    className="p-2 text-[#6db025] bg-[#6db025]/10 border border-[#6db025]/20 hover:bg-[#6db025]/20 hover:border-[#6db025]/40 rounded transition-colors"
                    title="Alege data"
                    aria-label="Alege data"
                  >
                    <Calendar size={20} />
                  </button>
                </PopoverTrigger>
                <PopoverContent
                  align="center"
                  className="w-auto p-0 bg-[#121212] border-white/10 text-white"
                >
                  <CalendarPicker
                    mode="single"
                    selected={parseISO(selectedDate)}
                    onSelect={(date) => {
                      if (date) setSelectedDate(format(date, 'yyyy-MM-dd'));
                    }}
                    disabled={(date) => date > new Date()}
                    locale={ro}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>

          <button
            onClick={() => setSelectedDate(format(addDays(parseISO(selectedDate), 1), 'yyyy-MM-dd'))}
            disabled={selectedDate >= todayStr}
            className="flex items-center justify-center gap-2 px-3 sm:px-4 py-2.5 bg-white/5 hover:bg-white/10 text-white/60 hover:text-white text-base font-black uppercase tracking-widest transition-colors rounded disabled:opacity-30"
            aria-label="Ziua următoare"
          >
            <span className="hidden sm:inline">Ziua următoare</span> <ChevronRight size={22} />
          </button>
        </div>
      </div>

      {loading ? (
        <div className="bg-[#0A0A0A] border border-white/5 p-20 text-center text-[#6db025] animate-pulse uppercase font-heading text-sm tracking-widest">
          Sincronizare date...
        </div>
      ) : attendance.length === 0 ? (
        <div className="bg-[#0A0A0A] border border-white/5 p-20 text-center flex flex-col items-center justify-center gap-4">
          <AlertCircle size={40} className="text-white/10" />
          <p className="text-white/20 font-heading uppercase text-base tracking-widest">Nicio prezență înregistrată în această zi</p>
        </div>
      ) : (
        <div className="space-y-8">
          {grouped.map(({ dayKey, timeframes }) => (
            <div key={dayKey} className="space-y-6">
              {timeframes.map(({ label: tfLabel, entries }) => {
                const m = tfLabel.match(/^(.+?)\s*\((.+)\)$/);
                const tfName = m ? m[1] : tfLabel;
                const tfRange = m ? m[2] : '';
                return (
                  <div key={tfLabel} className="space-y-3">
                    {/* Timeframe header */}
                    <div className="flex items-center gap-3 px-1">
                      <span className="text-white font-black uppercase tracking-widest text-base sm:text-lg">{tfName}</span>
                      {tfRange && (
                        <span className="text-white/40 text-base font-bold tracking-wider">({tfRange})</span>
                      )}
                    </div>

                    {/* Card grid */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                      {entries.map((att) => {
                        const methodLabel = (att.method || '').toUpperCase();
                        const isQR = methodLabel === 'QR';
                        const subLabel = att.person_type === 'child'
                          ? 'Copil'
                          : att.person_type === 'secondary_parent'
                            ? 'Partener'
                            : 'Adult';
                        return (
                          <div
                            key={att.id}
                            onClick={() => handleRowClick(att)}
                            className="relative bg-[#0F0F0F] border border-white/5 hover:border-white/10 hover:bg-[#121212] transition-colors cursor-pointer rounded overflow-hidden group"
                          >
                            {/* Green accent left border */}
                            <div className="absolute left-0 top-0 bottom-0 w-[3px] bg-[#6db025]" />

                            <div className="p-4 pl-5">
                              {/* Top: name + method badge */}
                              <div className="flex items-start justify-between gap-2 mb-3">
                                <div className="min-w-0 flex-1">
                                  <p className="text-white font-bold text-base uppercase tracking-tight truncate flex items-center gap-1.5">
                                    {att.person_type === 'child' && (
                                      <Baby size={16} className="text-purple-400 flex-shrink-0" />
                                    )}
                                    <span className="truncate">{att.person_name}</span>
                                  </p>
                                  <p className="text-white/50 text-xs uppercase font-black tracking-widest mt-1 truncate">
                                    {subLabel}
                                  </p>
                                </div>
                                {methodLabel && (
                                  <span className={`text-[11px] px-2 py-0.5 rounded font-black uppercase tracking-tighter flex-shrink-0 ${
                                    isQR
                                      ? 'bg-[#6db025]/15 text-[#6db025] border border-[#6db025]/20'
                                      : 'bg-white/5 text-white/50 border border-white/10'
                                  }`}>
                                    {methodLabel}
                                  </span>
                                )}
                              </div>

                              {/* Location + time */}
                              <div className="space-y-1.5 mb-3">
                                <div className="flex items-center gap-2 text-white/60 text-base">
                                  <MapPin size={16} className="text-[#6db025] flex-shrink-0" />
                                  <span className="truncate">{att.location}</span>
                                </div>
                                <div className="flex items-center gap-2 text-white text-base font-bold">
                                  <Clock size={16} className="text-white/50 flex-shrink-0" />
                                  <span>{format(new Date(att.recorded_at), 'HH:mm', { locale: ro })}</span>
                                </div>
                              </div>

                              {/* Actions */}
                              <div className="flex items-center justify-end gap-1.5 pt-2 border-t border-white/[0.04]">
                                <button
                                  onClick={(e) => { e.stopPropagation(); handleRowClick(att); }}
                                  className="p-2 text-white/40 hover:text-white hover:bg-white/5 transition-colors rounded"
                                  title="Vezi detalii"
                                >
                                  <Eye size={18} />
                                </button>
                                <button
                                  onClick={async (e) => {
                                    e.stopPropagation();
                                    if (window.confirm('Ștergi înregistrarea?')) {
                                      try {
                                        const res = await attendanceAPI.delete(att.id);
                                        toast.success(res.data?.message || 'Prezență ștearsă');
                                      } catch (err) {
                                        toast.error(err.response?.data?.detail || 'Eroare la ștergere');
                                      }
                                      fetchData();
                                    }
                                  }}
                                  className="p-2 text-red-500/50 hover:text-red-500 hover:bg-red-500/10 transition-colors rounded"
                                  title="Șterge"
                                >
                                  <Trash2 size={18} />
                                </button>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      )}

      {/* Attendance Detail Popup */}
      <Dialog open={!!popupAtt} onOpenChange={() => { setPopupAtt(null); setPopupData(null); }}>
        <DialogContent className="bg-[#121212] border-white/10 p-0 shadow-2xl max-w-sm overflow-hidden">
          <DialogHeader className="p-5 border-b border-white/5 bg-white/[0.02]">
            <DialogTitle className="font-heading text-lg text-white uppercase tracking-tight flex items-center gap-2">
              {popupAtt?.person_type === 'child' ? <Baby size={18} className="text-purple-400" /> : <User size={18} className="text-[#6db025]" />}
              {popupAtt?.person_name}
            </DialogTitle>
            {parentName && (
              <p className="text-white/30 text-[11px] uppercase font-black tracking-widest mt-1">
                Copil · Părinte:{' '}
                <button
                  onClick={() => {
                    const parentId = popupData?.user?.id;
                    if (parentId) {
                      setPopupAtt(null);
                      setPopupData(null);
                      navigate(`/admin/utilizatori/${parentId}`);
                    }
                  }}
                  className="text-[#6db025] hover:text-[#6db025]/70 hover:underline transition-colors"
                >
                  {parentName}
                </button>
                {parentEmail && <span className="ml-2 normal-case text-white/20">({parentEmail})</span>}
              </p>
            )}
          </DialogHeader>

          <div className="p-5 space-y-4">
            {/* Attendance info */}
            <div className="grid grid-cols-2 gap-2">
              <div className="p-3 bg-white/[0.03] border border-white/5">
                <p className="text-white/30 text-[10px] uppercase font-black tracking-widest mb-1">Data intrării</p>
                <p className="text-white text-base font-bold">
                  {popupAtt && format(new Date(popupAtt.recorded_at), 'dd MMM yyyy', { locale: ro })}
                </p>
                <p className="text-white/40 text-sm">
                  {popupAtt && format(new Date(popupAtt.recorded_at), 'HH:mm')}
                </p>
              </div>
              <div className="p-3 bg-white/[0.03] border border-white/5">
                <p className="text-white/30 text-[10px] uppercase font-black tracking-widest mb-1">Locație</p>
                <p className="text-white text-base font-bold">{popupAtt?.location}</p>
                <p className="text-white/30 text-[10px] uppercase font-black">{popupAtt?.method}</p>
              </div>
            </div>

            {/* Subscription info */}
            {popupLoading ? (
              <div className="p-4 text-center text-[#6db025] animate-pulse text-[11px] uppercase font-black tracking-widest">
                Se încarcă fișa...
              </div>
            ) : activeSub ? (
              <div className="p-4 bg-[#6db025]/5 border border-[#6db025]/20 space-y-3">
                <p className="text-[10px] text-[#6db025] uppercase font-black tracking-widest flex items-center gap-1.5">
                  <Check size={11} /> Abonament Activ
                </p>
                <p className="text-white font-bold text-base uppercase">{activeSub.plan_name}</p>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  {activeSub.sessions_total ? (
                    <>
                      <div className="text-center p-2 bg-black/20 rounded">
                        <p className={`text-2xl font-black ${activeSub.sessions_remaining <= 1 ? 'text-red-400' : activeSub.sessions_remaining <= 3 ? 'text-yellow-400' : 'text-[#6db025]'}`}>
                          {activeSub.sessions_remaining}
                        </p>
                        <p className="text-[10px] text-white/30 uppercase font-black mt-0.5">Rămase</p>
                      </div>
                      <div className="text-center p-2 bg-black/20 rounded">
                        <p className="text-2xl font-black text-white/40">{activeSub.sessions_total}</p>
                        <p className="text-[10px] text-white/30 uppercase font-black mt-0.5">Total</p>
                      </div>
                    </>
                  ) : activeSub.expires_at ? (
                    <div className="col-span-2 text-center p-2 bg-black/20 rounded">
                      <p className="text-white font-bold text-base">
                        {format(new Date(activeSub.expires_at), 'dd MMM yyyy', { locale: ro })}
                      </p>
                      <p className="text-[10px] text-white/30 uppercase font-black mt-0.5">Data Expirării</p>
                    </div>
                  ) : null}
                </div>
              </div>
            ) : popupData ? (
              <div className="p-4 bg-white/[0.02] border border-white/5 flex items-center gap-2">
                <CreditCard size={14} className="text-white/20" />
                <p className="text-white/30 text-[11px] uppercase font-black tracking-widest">Fără abonament activ</p>
              </div>
            ) : null}
          </div>
        </DialogContent>
      </Dialog>

      {/* Manual Registration Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="bg-[#121212] border-white/10 w-[calc(100vw-1rem)] max-w-lg h-[92dvh] sm:h-[90vh] flex flex-col p-0 overflow-hidden shadow-2xl rounded-xl gap-0">
          <DialogHeader className="p-4 sm:p-6 border-b border-white/5 bg-white/[0.02]">
            <DialogTitle className="text-white font-heading uppercase text-lg sm:text-xl">Înregistrare Manuală</DialogTitle>
          </DialogHeader>

          <div className="p-3 sm:p-6 space-y-3 sm:space-y-6 flex-1 flex flex-col overflow-hidden">
            {/* Location + Date row - side by side on mobile to save space */}
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-1 sm:gap-4">
              <div className="space-y-1">
                <label className="text-white/40 text-[11px] font-black uppercase tracking-widest">Locație</label>
                <select
                  value={selectedLocation}
                  onChange={(e) => setSelectedLocation(e.target.value)}
                  className="w-full bg-[#0A0A0A] border border-white/10 p-2 sm:p-4 text-white text-sm sm:text-base focus:border-[#6db025] outline-none transition-all appearance-none"
                >
                  {LOCATIONS.map(loc => <option key={loc.id} value={loc.name}>{loc.name}</option>)}
                </select>
              </div>

              <div className="space-y-1">
                <label className="text-white/40 text-[11px] font-black uppercase tracking-widest flex items-center justify-between">
                  Data/ora
                  {customDatetime && (
                    <button
                      onClick={() => setCustomDatetime('')}
                      className="text-[8px] text-white/20 hover:text-red-400 normal-case font-normal transition-colors"
                    >
                      ✕ reset
                    </button>
                  )}
                </label>
                <input
                  type="datetime-local"
                  value={customDatetime}
                  onChange={(e) => setCustomDatetime(e.target.value)}
                  placeholder="Acum"
                  className="w-full bg-[#0A0A0A] border border-white/10 p-2 sm:p-4 text-white text-sm sm:text-base focus:border-[#6db025] outline-none transition-all"
                />
              </div>
            </div>

            <div className="space-y-2 flex-1 flex flex-col overflow-hidden min-h-0">
              {/* Search + filters in a compact row */}
              <div className="flex gap-2 items-center">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-white/20" size={14} />
                  <input
                    type="text"
                    placeholder="Caută..."
                    value={searchUsers}
                    onChange={(e) => setSearchUsers(e.target.value)}
                    className="w-full bg-[#0A0A0A] border border-white/10 py-2 px-3 pl-9 text-sm sm:text-base text-white focus:border-[#6db025] outline-none placeholder:text-white/10"
                  />
                </div>
                <div className="flex gap-1 flex-shrink-0">
                  {[['all', 'Toți'], ['user', 'Adulți'], ['child', 'Copii']].map(([val, label]) => (
                    <button
                      key={val}
                      onClick={() => setTypeFilter(val)}
                      className={`px-2 sm:px-3 py-2 text-[11px] sm:text-sm font-black uppercase tracking-wider transition-colors rounded ${typeFilter === val ? 'bg-[#6db025] text-black' : 'bg-white/5 text-white/40 hover:bg-white/10'}`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Student list - compact rows that show 6-8+ on mobile */}
              <div className="flex-1 overflow-y-auto border border-white/5 bg-black/20 rounded-sm min-h-0">
                {(() => {
                  const filtered = activeUsers
                    .filter(p => typeFilter === 'all' || p.person_type === typeFilter)
                    .sort((a, b) => a.person_name.localeCompare(b.person_name, 'ro'));
                  return filtered.length > 0 ? filtered.map(person => {
                    const isSelected = selectedPersons.find(p => p.person_id === person.person_id);
                    return (
                      <div
                        key={`${person.person_type}-${person.person_id}`}
                        onClick={() => setSelectedPersons(prev => isSelected ? prev.filter(p => p.person_id !== person.person_id) : [...prev, person])}
                        className={`px-3 py-3 flex items-center gap-3 border-b border-white/5 cursor-pointer active:bg-white/10 hover:bg-white/5 transition-all ${isSelected ? 'bg-[#6db025]/10' : ''}`}
                      >
                        <div className={`w-6 h-6 border flex-shrink-0 flex items-center justify-center transition-all rounded-sm ${isSelected ? 'bg-[#6db025] border-[#6db025]' : 'border-white/15'}`}>
                          {isSelected && <Check size={14} className="text-black" />}
                        </div>
                        <p className="text-white text-sm sm:text-base font-bold uppercase tracking-tight truncate flex-1 min-w-0">{person.person_name}</p>
                        <span className="text-[10px] text-white/25 uppercase font-bold flex-shrink-0">
                          {person.person_type === 'child' ? 'Copil' : person.person_type === 'secondary_parent' ? 'Part.' : 'Adult'}
                        </span>
                      </div>
                    );
                  }) : (
                    <p className="p-10 text-center text-white/20 text-sm uppercase tracking-widest">Niciun membru găsit</p>
                  );
                })()}
              </div>
            </div>
          </div>

          <div className="p-3 sm:p-6 bg-[#0A0A0A] border-t border-white/5">
            <button
              onClick={handleRecordAttendance}
              disabled={saving || selectedPersons.length === 0}
              className="btn-primary w-full py-3 sm:py-5 text-sm sm:text-base font-black uppercase tracking-[0.2em] disabled:opacity-20"
            >
              {saving ? 'Se procesează...' : `Confirmă intrarea (${selectedPersons.length})`}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AttendancePage;
